import React, { ChangeEvent, useState } from "react";

// Define filter type for configuration
interface FilterConfig {
  name: keyof FilterValues;
  label: string;
  type: "text" | "select";
  options?: string[];
}

interface FilterValues {
  title: string;
  institution: string;
  author: string;
  year: string;
  sort: string;
}

interface SearchBarProps {
  filters: FilterConfig[];
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  filterValues: FilterValues;
  setFilterValues: (filters: FilterValues) => void;
  onSearch: (query: string, filters: FilterValues) => void;
}

const SearchBar: React.FC<SearchBarProps> = ({
  filters,
  searchQuery,
  setSearchQuery,
  filterValues,
  setFilterValues,
  onSearch,
}) => {
  const [showFilters, setShowFilters] = useState(false);

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleFilterChange = (
    e: ChangeEvent<HTMLInputElement | HTMLSelectElement>,
    filterName: keyof FilterValues
  ) => {
    setFilterValues({
      ...filterValues,
      [filterName]: e.target.value,
    });
  };

  const handleSearch = () => {
    setShowFilters(false);
    onSearch(searchQuery, filterValues);
  };

  return (
    <div className="w-full px-6 my-4">
      {/* General Search Input */}
      <div className="flex items-center rounded-md mb-4">
        <input
          type="text"
          value={searchQuery}
          onChange={handleSearchChange}
          placeholder="Search..."
          className="w-full p-2 text-gray-700 rounded-md focus:outline-none focus:ring-4 border border-gray-300 "
        />
        <button
          onClick={handleSearch}
          className="ml-1 text-white px-4 py-2 bg-blue-500 hover:ring-1 rounded"
        >
          <i className="fa fa-search"></i>
        </button>
        <button
          onClick={
            showFilters
              ? () => setShowFilters(false)
              : () => setShowFilters(true)
          }
          className={`ml-1 px-4 py-2 ${
            showFilters
              ? "bg-blue-500 text-white"
              : "border-blue-500 border text-accent"
          } hover:ring-1 rounded`}
        >
          <i className="fa fa-filter"></i>
        </button>
      </div>

      {/* Dynamic Filter Inputs */}
      {showFilters && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
          {filters.map((filter) => (
            <div key={filter.name} className="flex flex-col">
              <label className="text-sm mb-1">{filter.label}</label>
              {filter.type === "text" ? (
                <input
                  type="text"
                  value={filterValues[filter.name]}
                  onChange={(e) => handleFilterChange(e, filter.name)}
                  className="p-2 border border-gray-300 rounded-md focus:outline-none"
                />
              ) : (
                <select
                  value={filterValues[filter.name]}
                  onChange={(e) => handleFilterChange(e, filter.name)}
                  className="p-2 border text-primary border-gray-300 rounded-md focus:outline-none"
                >
                  <option value="">Select {filter.label.toLowerCase()}</option>
                  {filter.options?.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
